import { $getRoot, CLEAR_EDITOR_COMMAND, EditorState, LexicalEditor } from 'lexical';
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListItemNode, ListNode } from '@lexical/list';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MentionNode } from './nodes/MentionNode';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import RefPlugin from './plugins/RefPlugin';
import AutofocusPlugin from './plugins/AutoFocus';
import SubmitKeyPlugin from './plugins/SubmitKeyPlugin';
import FocusBlurPlugin from './plugins/FocusBlurPlugin';

export type EditorRef = {
    clear: () => void;
};

type EditorProps = {
    defaultContent?: string;
    placeholder?: string;
    isReplyEditor?: boolean;
    isReplyContent?: boolean;
    readOnly?: boolean;
    maxHeight?: string;
    errorMessage?: string;
    hideBorder?: boolean;
    hideToolbar?: boolean;
    onContentChange?: (rawContent: string, structuredContent: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    autoFocus?: boolean;
};

const Editor = forwardRef(function Editor(
    {
        defaultContent,
        placeholder,
        isReplyEditor,
        isReplyContent,
        readOnly = false,
        maxHeight,
        onContentChange,
        errorMessage,
        hideBorder,
        hideToolbar,
        onBlur,
        onFocus,
        autoFocus = true,
    }: EditorProps,
    ref: React.ForwardedRef<EditorRef>
): JSX.Element {
    const editorRef = useRef<LexicalEditor | null>(null);
    const focusRef = useRef(false);
    const placeholderRef = useRef<HTMLLabelElement>(null);

    function onChange(state: EditorState, editor: LexicalEditor): void {
        !readOnly &&
            state.read(() => {
                const root = $getRoot();
                onContentChange && onContentChange(root.getTextContent(), JSON.stringify(editor.getEditorState()));
            });
    }

    const EDITABLE_EDITOR_STYLE = `${isReplyEditor ? maxHeight || 'h-[100px]' : 'h-[250px]'} ${
        hideToolbar ? 'p-2' : 'p-4'
    } outline-indigo-500 `;
    const READ_ONLY_TITLE_STYLE = `${maxHeight ? `whitespace-pre-wrap max-h-[${maxHeight}]` : 'line-clamp-2'} pl-1 pt-2`;
    const READ_ONLY_REPLY_STYLE = 'whitespace-pre-wrap pl-1 pt-2';

    useEffect(() => {
        if (editorRef.current) {
            if (defaultContent) {
                editorRef.current.setEditorState(editorRef.current.parseEditorState(defaultContent));

                editorRef.current.focus();
            }
        }
    }, [defaultContent, autoFocus]);

    useImperativeHandle(
        ref,
        () => ({
            clear: (): void => {
                if (editorRef.current) {
                    editorRef.current.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
                }
            },
        }),
        [editorRef]
    );

    return (
        <>
            <div
                className={`focus-within:border-indigo-600 flex flex-col h-full bg-white ${readOnly ? '' : 'rounded-md border shadow-sm'}`}
            >
                <LexicalComposer
                    key={`editor-${readOnly}`}
                    initialConfig={{
                        editable: !readOnly,
                        editorState: defaultContent,
                        namespace: 'Editor',
                        theme: {
                            paragraph: 'text-sm',
                            text: {
                                bold: 'font-bold',
                                italic: 'italic',
                                underline: 'underline',
                            },
                            list: {
                                ol: 'list-decimal ml-4 text-sm',
                                ul: 'list-disc ml-4 text-sm',
                            },
                        },
                        nodes: [ListNode, ListItemNode, MentionNode],
                        onError(error): void {
                            throw error;
                        },
                    }}
                >
                    {readOnly || hideToolbar ? <></> : <ToolbarPlugin />}
                    <div
                        className="relative h-full flex flex-col justify-start"
                        style={{ minHeight: (placeholderRef.current?.offsetHeight || 0) + 16 }}
                    >
                        <RichTextPlugin
                            contentEditable={<ContentEditable className="outline-none p-2 relative overflow-auto flex-1 h-full" />}
                            placeholder={
                                <label
                                    ref={placeholderRef}
                                    className="absolute top-2 left-2 text-sm text-gray-500 z-0 cursor-text"
                                    onClick={(): void => {
                                        editorRef.current?.focus();
                                    }}
                                >
                                    {placeholder || 'Enter some text...'}
                                </label>
                            }
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                    </div>
                    <RefPlugin editorRef={editorRef} />
                    <MentionsPlugin />
                    <OnChangePlugin onChange={onChange} />
                    <HistoryPlugin />
                    <ListPlugin />
                    <ClearEditorPlugin />
                    <AutofocusPlugin disabled={readOnly || !autoFocus} />
                    <SubmitKeyPlugin />
                    {/* <FocusBlurPlugin onBlur={onBlur} onFocus={onFocus} /> */}
                </LexicalComposer>
            </div>
            <p className="text-red-600 text-sm">{errorMessage && errorMessage}</p>
        </>
    );
});

export default Editor;
